import { memo, FC, SVGProps, useMemo, useState, useLayoutEffect, startTransition } from "react"

import { MotionConfig } from "motion/react"
import * as m from "motion/react-m"

// https://stackoverflow.com/a/52490977
type Tuple<T, N extends number> = N extends N ? (number extends N ? T[] : _TupleOf<T, N, []>) : never
type _TupleOf<T, N extends number, R extends unknown[]> = R["length"] extends N ? R : _TupleOf<T, N, [T, ...R]>

const CONFIGS: Record<string, Tuple<1 | 0, 25>> = {
  UNLINKED: [1, 0, 0, 0, 1, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 1, 0, 0, 0, 1],
  LOADING: [0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1, 1, 0, 0, 0, 1, 0, 0],
  TICK: [0, 0, 0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0],
  QUESTION: [0, 1, 1, 1, 0, 0, 1, 0, 1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
  UPGRADE: [0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 0, 0, 0, 0, 0],
  BELL: [0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0, 0, 1, 0, 0],
  HOURGLASS: [1, 1, 1, 1, 1, 0, 1, 1, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
  NOTE: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 1, 1, 1, 1, 1],
  CHAT: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 1, 0, 0, 0, 0],
  CALENDAR: [0, 1, 0, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, 1, 1],
  UPLOAD: [0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
  DIAGONAL: [0, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
  PLUS: [0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 1, 1, 1, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
  MAGNIFY: [1, 1, 1, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1],
}

const X_POSITIONS = [4.5, 10.5, 16.5, 22.5, 28.5]
const Y_POSITIONS = [5, 11, 17, 23, 29]

const POSITIONS = Y_POSITIONS.flatMap((y) => X_POSITIONS.map((x) => [x, y] as const))

export const ContextLargeIcon: FC<
  SVGProps<SVGSVGElement> & { config: keyof typeof CONFIGS | "LAVALAMP" | Tuple<boolean | 1 | 0, 25> }
> = memo(({ config: _config = "UNLINKED", ...props }) => {
  const [animationIter, setAnimationIter] = useState(0)

  // TODO: Use motion `animate` function to bypass React state updates
  useLayoutEffect(() => {
    if (_config === "LAVALAMP") {
      const interval = setInterval(() => {
        startTransition(() => {
          setAnimationIter((iter) => iter + 1)
        })
      }, 200)
      return () => clearInterval(interval)
    }
    return () => {}
  }, [_config])
  const config = useMemo(() => {
    if (_config === "LAVALAMP") {
      return Array(25)
        .fill(0)
        .map(() => Math.round(Math.random()))
    }
    return Array.isArray(_config) ? _config : CONFIGS[_config]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_config, animationIter])

  const delays = useMemo(
    () =>
      Array(25)
        .fill(0)
        .map((_) => Math.random() * 0.6),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_config, animationIter]
  )
  return (
    <MotionConfig transition={{ duration: _config === "LAVALAMP" ? 0.8 : 0.15, type: "tween", ease: [0.3, 0, 0.2, 1] }}>
      <svg width="33" height="34" viewBox="0 0 33 34" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
        {POSITIONS.map(([cx, cy], i) => {
          const highlighted = !!config?.[i]
          return (
            <m.circle
              key={i}
              cx={cx}
              cy={cy}
              initial={{ r: 1.5, opacity: 0.2 }}
              animate={{ r: highlighted ? 1.65 : 1.5, opacity: highlighted ? 1 : 0.2 }}
              transition={{ delay: delays[i] }}
            />
          )
        })}
      </svg>
    </MotionConfig>
  )
})

ContextLargeIcon.displayName = "ContextLargeIcon"
export default ContextLargeIcon
